import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FiArrowLeft,
  FiCheck,
  FiClock,
  FiRefreshCw,
  FiShield,
} from "react-icons/fi";
import styles from "./ForgotOTP.module.css";
import api from "../../services/axiosConfig";
import { OTP_SEND, OTP_SEND_FORGOT, OTP_VERIFIED_FORGOT } from "../../constant-value";

const ForgotOTP = () => {
  const { mobile } = useParams();
  const navigate = useNavigate();

  const inputsRef = useRef([]);
  const timerRef = useRef(null);

  const [cooldown, setCooldown] = useState(180);
  const [otpValues, setOtpValues] = useState([
    "",
    "",
    "",
    "",
  ]);

  const [errorShake, setErrorShake] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);

  // -----------------------------
  // Timer
  // -----------------------------

  const setTimer = () => {
    clearInterval(timerRef.current);

    setCooldown(180);

    timerRef.current = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          timerRef.current = null;
          return 0;
        }

        return prev - 1;
      });
    }, 1000);
  };

  useEffect(() => {
    setTimer();
    inputsRef.current[0]?.focus();

    return () => {
      clearInterval(timerRef.current);
    };
  }, []);

  // -----------------------------
  // OTP Input
  // -----------------------------

  const handleInput = (event, index) => {
    const value = event.target.value
      .replace(/\D/g, "")
      .slice(0, 1);

    const nextValues = [...otpValues];

    nextValues[index] = value;

    setOtpValues(nextValues);

    if (navigator.vibrate && value) {
      navigator.vibrate(20);
    }

    if (value && index < 3) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (event, index) => {
    if (
      event.key === "Backspace" &&
      !otpValues[index] &&
      index > 0
    ) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  // -----------------------------
  // Clear OTP
  // -----------------------------

  const clearOtp = () => {
    setOtpValues(["", "", "", ""]);

    inputsRef.current.forEach((input) => {
      if (input) input.value = "";
    });

    inputsRef.current[0]?.focus();
  };

  // -----------------------------
  // OTP Error
  // -----------------------------

  const showOtpError = (text) => {
    toast.warning(text, {
      autoClose: 1500,
    });

    setErrorShake(true);

    setTimeout(() => {
      setErrorShake(false);
    }, 400);

    clearOtp();
  };

  // -----------------------------
  // Verify OTP
  // -----------------------------

  const continueHandler = (event) => {
    event.preventDefault();

    const otpCode = otpValues.join("");

    if (otpCode.length !== 4) {
      toast.warning(
        "لطفاً کد ۴ رقمی را کامل وارد کنید.",
        {
          autoClose: 1500,
        }
      );

      inputsRef.current[0]?.focus();

      return;
    }

    if (isVerifying) return;

    setIsVerifying(true);

    api
      .post(`/${OTP_VERIFIED_FORGOT}`, {
        phoneNumber: mobile,
        otpCode,
      })
      .then((res) => {
        const data = res.data;

        if (data.isSuccess) {
          toast.success(data.message, {
            autoClose: 1500,
          });

          navigate(`/forgot-change/${mobile}`);

          return;
        }

        showOtpError(data.message);
      })
      .catch((error) => {
        console.log("FORGOT OTP VERIFY ERROR:", error);

        showOtpError(
          error.response?.data?.message ||
            "خطایی در تأیید کد رخ داده است."
        );
      })
      .finally(() => {
        setIsVerifying(false);
      });
  };

  // -----------------------------
  // Re-send OTP
  // -----------------------------

  const reSendOtp = () => {
    if (cooldown > 0 || isResending) return;

    setIsResending(true);
//??????????????????????????????????????????????????
    api
      .post(`/${OTP_SEND_FORGOT}`, {
        phoneNumber: mobile,
      })
      .then((res) => {
        const data = res.data;

        if (data.isSuccess) {
          toast.success(data.message, {
            autoClose: 1500,
          });

          setTimer();
          clearOtp();

          return;
        }

        toast.warning(data.message, {
          autoClose: 1500,
        });
      })
      .catch((error) => {
        console.log("FORGOT OTP RESEND ERROR:", error);

        toast.warning(
          error.response?.data?.message ||
            "امکان ارسال مجدد کد وجود ندارد.",
          {
            autoClose: 1500,
          }
        );
      })
      .finally(() => {
        setIsResending(false);
      });
  };

  const reSendHandler = (event) => {
    event.preventDefault();
    reSendOtp();
  };

  // -----------------------------
  // Back
  // -----------------------------

  const backHandler = (event) => {
    event.preventDefault();
    navigate(-1);
  };

  // -----------------------------
  // UI
  // -----------------------------

  return (
    <div className={styles.page} dir="rtl">
      <div className={styles.container}>

        {/* Header */}

        <div className={styles.header}>
          <div className={styles.iconBox}>
            <FiShield />
          </div>

          <div className={styles.step}>
            گام دوم
          </div>

          <h1>تأیید شماره همراه</h1>

          <p>
            کد ۴ رقمی به شماره
            <strong>{mobile}</strong>
            ارسال شد
          </p>
        </div>

        {/* OTP */}

        <div
          className={`${styles.otpWrapper} ${
            errorShake ? styles.shake : ""
          }`}
          dir="ltr"
        >
          {otpValues.map((value, index) => (
            <input
              key={index}
              ref={(element) => {
                inputsRef.current[index] = element;
              }}
              type="text"
              inputMode="numeric"
              maxLength={1}
              autoComplete={
                index === 0
                  ? "one-time-code"
                  : "off"
              }
              value={value}
              onChange={(event) =>
                handleInput(event, index)
              }
              onKeyDown={(event) =>
                handleKeyDown(event, index)
              }
              className={styles.otpInput}
            />
          ))}
        </div>

        <div className={styles.helper}>
          لطفاً کد ۴ رقمی ارسال‌شده را وارد کنید
        </div>

        {/* Verify */}

        <button
          type="button"
          className={styles.verifyButton}
          onClick={continueHandler}
          disabled={isVerifying}
        >
          {isVerifying ? (
            <>
              <span className={styles.spinner} />
              در حال بررسی...
            </>
          ) : (
            <>
              <FiCheck />
              تأیید و ادامه
            </>
          )}
        </button>

        {/* Actions */}

        <div className={styles.actions}>

          <button
            type="button"
            className={styles.backButton}
            onClick={backHandler}
          >
            <FiArrowLeft />
            اصلاح شماره
          </button>

          <button
            type="button"
            className={styles.resendButton}
            onClick={reSendHandler}
            disabled={
              cooldown > 0 || isResending
            }
          >
            {isResending ? (
              <>
                <FiRefreshCw
                  className={styles.rotating}
                />
                در حال ارسال...
              </>
            ) : cooldown > 0 ? (
              <>
                <FiClock />
                {cooldown} ثانیه
              </>
            ) : (
              <>
                <FiRefreshCw />
                ارسال مجدد
              </>
            )}
          </button>

        </div>

        {/* Footer */}

        <div className={styles.footer}>
          <span>
            کد را دریافت نکردید؟
          </span>

          <span>
            بعد از پایان زمان، امکان ارسال مجدد فعال می‌شود.
          </span>
        </div>

      </div>
    </div>
  );
};

export default ForgotOTP;